import { Alert, Button, LinearProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useRetryProposalPackageSendRecipients from "hooks/proposals/useRetryProposalPackageSendRecipients";

type ProposalPackageSendProgressProps = {
  packageSummary: any;
};

export default function ProposalPackageSendProgress({
  packageSummary,
}: ProposalPackageSendProgressProps) {
  const run = packageSummary?.latestPackageSendRun;
  const [retryFailedRecipients, { loading: retrying }] = useRetryProposalPackageSendRecipients();

  if (!run) {
    return null;
  }

  const isQueued = run.status === "QUEUED";
  const isProcessing = run.status === "PROCESSING";
  const isActive = isQueued || isProcessing;
  const totalRecipients = run.totalRecipients ?? 0;
  const processedRecipients = run.processedRecipients ?? 0;
  const progress = totalRecipients > 0 ? (processedRecipients / totalRecipients) * 100 : 0;

  let severity: "info" | "success" | "warning" = "info";
  let title = "Preparing proposal sends";
  let message = `Preparing sends to ${totalRecipients} companies.`;

  if (isProcessing) {
    title = "Sending proposals";
    message = `Sending proposals: ${processedRecipients} of ${totalRecipients} companies.`;
  } else if (run.status === "COMPLETED") {
    severity = "success";
    title = "Proposal sends complete";
    message = `All ${totalRecipients} companies received the proposal.`;
  } else if (run.status === "COMPLETED_WITH_ERRORS") {
    severity = "warning";
    title = "Some proposal sends need attention";
    message = `${run.succeededRecipients} of ${totalRecipients} companies received the proposal. ${run.failedRecipients} failed.`;
  } else if (run.status === "CANCELED") {
    severity = "warning";
    title = "Proposal sending canceled";
    message = "The remaining company proposal sends were canceled.";
  }

  return (
    <Alert
      severity={severity}
      action={
        run.status === "COMPLETED_WITH_ERRORS" && run.failedRecipients > 0 ? (
          <Button
            color="inherit"
            size="small"
            disabled={retrying}
            onClick={() => retryFailedRecipients({ variables: { runId: run.id } })}
          >
            Retry failed companies
          </Button>
        ) : undefined
      }
      sx={{ alignItems: "flex-start" }}
    >
      <MDBox width="100%">
        <MDTypography variant="h6" fontWeight="medium">
          {title}
        </MDTypography>
        <MDTypography variant="button" color="text">
          {message}
        </MDTypography>
        {isActive && (
          <>
            <LinearProgress
              variant={isQueued ? "indeterminate" : "determinate"}
              value={isQueued ? undefined : progress}
              sx={{
                mt: 1.5,
                mb: 1,
                backgroundColor: "rgba(0, 148, 198, 0.18)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#0094C6",
                },
              }}
            />
            <MDTypography variant="caption" color="text">
              This can take a few minutes. You can safely leave this page; sending will continue in
              the background.
            </MDTypography>
          </>
        )}
      </MDBox>
    </Alert>
  );
}
